import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { handleApiError } from '../../utils/errorHandler';
import folderDocumentsApi, {
  DocumentFolderDto,
  FolderDocumentDto,
  FolderDocumentRequestDto,
} from '../../services/folderDocumentService';
import { contractApi, ClientContractDocumentsDto } from '../../services/contractService';
import { hexToRgba } from '../../utils/colorUtils';
import FolderDocumentsGrid from './FolderDocumentsGrid';
import CreateFolderModal from './CreateFolderModal';
import RenameFolderModal from './RenameFolderModal';
import DeleteFolderConfirmModal from './DeleteFolderConfirmModal';
import FolderDocumentRequestsList from './FolderDocumentRequestsList';
import NewDocumentRequestModal from './NewDocumentRequestModal';
import ContractDocumentsFolder from './ContractDocumentsFolder';
import ContractDocumentsGrid from './ContractDocumentsGrid';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  height: 100%;
  overflow-y: auto;
`;

const Section = styled.div`
  padding: 8px 0 24px 0;
  /* No box styling - unified canvas */
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-md);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RefreshIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 11-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
    <path d="M21 3v5h-5"/>
  </svg>
);

const RenameIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3,6 5,6 21,6"/>
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

// Windows Explorer-like folder grid
const FoldersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: stretch;
`;

const FolderItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100px;
  cursor: pointer;
  padding: 12px 8px;
  border-radius: var(--border-radius-md);
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: var(--background-secondary);
  }
`;

const FolderIconLarge = styled.div`
  font-size: 48px;
  margin-bottom: 8px;
  line-height: 1;
`;

const FolderName = styled.div`
  font-size: 12px;
  text-align: center;
  color: var(--text-primary);
  word-break: break-word;
  line-height: 1.3;
  max-width: 100%;
`;

const FolderCount = styled.div`
  font-size: 11px;
  color: var(--text-secondary);
  text-align: center;
`;

const NewBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #16a34a;
  color: white;
  font-size: 9px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 8px;
`;

const AddFolderItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100px;
  min-height: 100px;
  cursor: pointer;
  padding: 12px 8px;
  border-radius: var(--border-radius-md);
  border: 2px dashed var(--border-color);
  transition: all 0.2s ease;
  color: var(--text-secondary);

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    background: ${hexToRgba('#008EA2', 0.05)};
  }
`;

const AddFolderIcon = styled.div`
  font-size: 32px;
  margin-bottom: 6px;
  line-height: 1;
`;

const AddFolderText = styled.div`
  font-size: 11px;
  text-align: center;
`;

const FolderMenuButton = styled.button`
  position: absolute;
  top: 4px;
  left: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;

  ${FolderItem}:hover & {
    opacity: 1;
  }

  &:hover {
    background: var(--background-primary);
    color: var(--text-primary);
  }
`;

const FolderMenuDropdown = styled.div`
  position: absolute;
  top: 28px;
  left: 4px;
  background: white;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 120px;
  overflow: hidden;
`;

const FolderMenuItem = styled.button<{ danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  font-size: 13px;
  color: ${props => props.danger ? '#dc2626' : 'var(--text-primary)'};
  cursor: pointer;
  text-align: right;

  &:hover {
    background: var(--background-secondary);
  }
`;

// Divider between folders and requests
const Divider = styled.div`
  height: 1px;
  background: var(--border-color);
  margin: 16px 0 24px 0;
`;

// New request button - outlined style like "הוסף פגישה"
const NewRequestButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 20px;

  &:hover {
    background: var(--primary-color);
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background: transparent;
      color: var(--primary-color);
    }
  }
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-md);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: var(--primary-color);
  }
`;

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6"/>
  </svg>
);

const BreadcrumbSeparator = styled.span`
  color: var(--text-secondary);
`;

const BreadcrumbCurrent = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: var(--text-secondary);
`;

const EmptyFoldersText = styled.div`
  color: var(--text-secondary);
  font-size: 14px;
  padding: 20px 0;
`;


// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface NewDocumentsTabProps {
  clientId: number;
  clientName?: string;
  folderId?: number;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const NewDocumentsTab: React.FC<NewDocumentsTabProps> = ({ clientId, clientName, folderId }) => {
  const navigate = useNavigate();

  // State - viewMode is derived from folderId prop
  const [folders, setFolders] = useState<DocumentFolderDto[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<DocumentFolderDto | null>(null);
  const [documents, setDocuments] = useState<FolderDocumentDto[]>([]);
  const [requests, setRequests] = useState<FolderDocumentRequestDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Contract documents state
  const [contractDocuments, setContractDocuments] = useState<ClientContractDocumentsDto | null>(null);
  const [showContractDocuments, setShowContractDocuments] = useState(false);

  // Modal states
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState<DocumentFolderDto | null>(null);

  // Folder menu state
  const [openMenuFolderId, setOpenMenuFolderId] = useState<number | null>(null);

  // Derive viewMode from folderId prop and showContractDocuments state
  const viewMode = showContractDocuments ? 'contract-documents' : (folderId ? 'folder-contents' : 'folders');

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const loadFolders = useCallback(async () => {
    try {
      const data = await folderDocumentsApi.getFolders(clientId);
      setFolders(data);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטעינת התיקיות'), { id: 'docs-load' });
    }
  }, [clientId]);

  const loadDocuments = useCallback(async (folderId: number) => {
    try {
      const data = await folderDocumentsApi.getDocumentsByFolder(folderId);
      setDocuments(data);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטעינת המסמכים'));
    }
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const data = await folderDocumentsApi.getRequestsByClient(clientId);
      setRequests(data);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטעינת הבקשות'), { id: 'docs-load' });
    }
  }, [clientId]);

  const loadContractDocuments = useCallback(async () => {
    try {
      const data = await contractApi.getContractDocumentsByClientId(clientId);
      setContractDocuments(data);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטעינת מסמכי החוזה'), { id: 'docs-load' });
    }
  }, [clientId]);

  // Initial data load - loads folders, requests, and contract documents
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([loadFolders(), loadRequests(), loadContractDocuments()]);
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialData();
  }, [clientId, loadFolders, loadRequests, loadContractDocuments]);

  // Load folder when folderId changes or folders are loaded
  useEffect(() => {
    if (folderId && folders.length > 0) {
      const folder = folders.find(f => f.id === folderId);
      if (folder) {
        setSelectedFolder(folder);
        loadDocuments(folderId);
      }
    } else if (!folderId) {
      setSelectedFolder(null);
      setDocuments([]);
    }
  }, [folderId, folders, loadDocuments]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuFolderId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (folderId) {
        await loadDocuments(folderId);
      } else {
        await Promise.all([loadFolders(), loadRequests()]);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  // ============================================================================
  // FOLDER HANDLERS
  // ============================================================================

  const handleFolderClick = (folder: DocumentFolderDto) => {
    // Navigate to folder URL - this will update folderId prop
    navigate(`/clients/${clientId}/manage/documents/${folder.id}`);
  };

  const handleBackToFolders = () => {
    // Reset contract documents view if active
    if (showContractDocuments) {
      setShowContractDocuments(false);
      return;
    }
    // Navigate back to documents tab without folderId
    navigate(`/clients/${clientId}/manage/documents`);
  };

  const handleContractFolderClick = () => {
    setShowContractDocuments(true);
  };

  const handleCreateFolder = async (name: string) => {
    try {
      await folderDocumentsApi.createFolder({ clientId, name });
      await loadFolders();
      setShowCreateFolderModal(false);
    } catch (error: any) {
      throw error;
    }
  };

  const handleRenameFolder = async (newName: string) => {
    if (!folderToEdit) return;
    try {
      await folderDocumentsApi.updateFolder(folderToEdit.id, { name: newName });
      await loadFolders();
      setShowRenameFolderModal(false);
      setFolderToEdit(null);
    } catch (error: any) {
      throw error;
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToEdit) return;
    const wasSelectedFolder = folderId === folderToEdit.id;
    try {
      await folderDocumentsApi.deleteFolder(folderToEdit.id);
      await loadFolders();
      setShowDeleteFolderModal(false);
      setFolderToEdit(null);
      if (wasSelectedFolder) {
        handleBackToFolders();
      }
    } catch (error: any) {
      throw error;
    }
  };

  const handleFolderMenuClick = (e: React.MouseEvent, folderId: number) => {
    e.stopPropagation();
    setOpenMenuFolderId(openMenuFolderId === folderId ? null : folderId);
  };

  const handleFolderMenuAction = (e: React.MouseEvent, action: string, folder: DocumentFolderDto) => {
    e.stopPropagation();
    setOpenMenuFolderId(null);
    setFolderToEdit(folder);
    switch (action) {
      case 'rename':
        setShowRenameFolderModal(true);
        break;
      case 'delete':
        setShowDeleteFolderModal(true);
        break;
    }
  };

  // ============================================================================
  // DOCUMENT HANDLERS
  // ============================================================================

  const handleUploadDocument = async (file: File, notes?: string) => {
    if (!selectedFolder) return;
    try {
      await folderDocumentsApi.uploadDocument({
        clientId,
        folderId: selectedFolder.id,
        file,
        notes,
      });
      await loadDocuments(selectedFolder.id);
      await loadFolders();
    } catch (error: any) {
      throw error;
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      await folderDocumentsApi.deleteDocument(documentId);
      if (selectedFolder) {
        await loadDocuments(selectedFolder.id);
        await loadFolders();
      }
    } catch (error: any) {
      throw error;
    }
  };

  const handleDownloadDocument = (documentId: number) => {
    folderDocumentsApi.downloadDocument(documentId);
  };

  // ============================================================================
  // REQUEST HANDLERS
  // ============================================================================

  const handleCreateRequest = async () => {
    await loadRequests();
    setShowNewRequestModal(false);
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderFoldersView = () => (
    <>
      {/* Folders Section */}
      <SectionHeader>
        <SectionTitle>תיקיות</SectionTitle>
        <RefreshButton onClick={handleRefresh} disabled={isRefreshing} title="רענון">
          <RefreshIcon />
        </RefreshButton>
      </SectionHeader>

      <FoldersRow>
        {/* Contract Documents Folder - displayed first */}
        {contractDocuments?.hasContract && (
          <ContractDocumentsFolder
            contractDocuments={contractDocuments}
            onClick={handleContractFolderClick}
          />
        )}

        {/* User Folders */}
        {folders.map(folder => (
          <FolderItem key={folder.id} onClick={() => handleFolderClick(folder)}>
            <FolderMenuButton onClick={(e) => handleFolderMenuClick(e, folder.id)}>
              ⋮
            </FolderMenuButton>
            {openMenuFolderId === folder.id && (
              <FolderMenuDropdown onClick={(e) => e.stopPropagation()}>
                <FolderMenuItem onClick={(e) => handleFolderMenuAction(e, 'rename', folder)}>
                  <RenameIcon /> שנה שם
                </FolderMenuItem>
                <FolderMenuItem danger onClick={(e) => handleFolderMenuAction(e, 'delete', folder)}>
                  <TrashIcon /> מחק
                </FolderMenuItem>
              </FolderMenuDropdown>
            )}
            {folder.newDocumentCount > 0 && <NewBadge>+{folder.newDocumentCount}</NewBadge>}
            <FolderIconLarge>📁</FolderIconLarge>
            <FolderName>{folder.name}</FolderName>
            <FolderCount>({folder.documentCount})</FolderCount>
          </FolderItem>
        ))}

        {/* Add folder button at the end */}
        <AddFolderItem onClick={() => setShowCreateFolderModal(true)}>
          <AddFolderIcon>+</AddFolderIcon>
          <AddFolderText>תיקיה חדשה</AddFolderText>
        </AddFolderItem>
      </FoldersRow>

      {folders.length === 0 && (
        <EmptyFoldersText>אין תיקיות עדיין. לחץ על "תיקיה חדשה" כדי ליצור את התיקיה הראשונה.</EmptyFoldersText>
      )}

      <Divider />

      {/* New Request Button */}
      <NewRequestButton
        onClick={() => setShowNewRequestModal(true)}
        disabled={folders.length === 0}
        title={folders.length === 0 ? 'יש ליצור תיקיה לפני יצירת בקשה' : ''}
      >
        <Plus size={16} strokeWidth={2} />
        בקשת מסמכים חדשה
      </NewRequestButton>

      {/* Requests List */}
      <FolderDocumentRequestsList
        requests={requests}
        folders={folders}
        clientId={clientId}
        onRefresh={loadRequests}
      />
    </>
  );

  const renderFolderContentsView = () => (
    <>
      <Breadcrumb>
        <BackButton onClick={handleBackToFolders} title="חזרה לתיקיות">
          <BackIcon />
        </BackButton>
        <BreadcrumbSeparator>/</BreadcrumbSeparator>
        <BreadcrumbCurrent>📁 {selectedFolder?.name}</BreadcrumbCurrent>
      </Breadcrumb>

      <FolderDocumentsGrid
        documents={documents}
        onUpload={handleUploadDocument}
        onDelete={handleDeleteDocument}
        onDownload={handleDownloadDocument}
        onConvertToPdf={folderDocumentsApi.convertToPdf}
        onRefresh={() => folderId && loadDocuments(folderId)}
        isLoading={isRefreshing}
      />
    </>
  );

  const renderContractDocumentsView = () => (
    <>
      <Breadcrumb>
        <BackButton onClick={handleBackToFolders} title="חזרה לתיקיות">
          <BackIcon />
        </BackButton>
        <BreadcrumbSeparator>/</BreadcrumbSeparator>
        <BreadcrumbCurrent>📁 מסמכי חוזה</BreadcrumbCurrent>
      </Breadcrumb>

      <ContractDocumentsGrid
        documents={contractDocuments?.documents || []}
        contractNumber={contractDocuments?.contractNumber || ''}
        onBack={handleBackToFolders}
        onConvertToPdf={contractApi.convertDocumentToPdf}
        onRefresh={loadContractDocuments}
      />
    </>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (isLoading) {
    return (
      <TabContainer>
        <LoadingOverlay>טוען...</LoadingOverlay>
      </TabContainer>
    );
  }

  const renderContent = () => {
    switch (viewMode) {
      case 'contract-documents':
        return renderContractDocumentsView();
      case 'folder-contents':
        return renderFolderContentsView();
      default:
        return renderFoldersView();
    }
  };

  return (
    <TabContainer>
      <Section>
        {renderContent()}
      </Section>

      {/* Modals */}
      {showCreateFolderModal && (
        <CreateFolderModal
          onClose={() => setShowCreateFolderModal(false)}
          onCreate={handleCreateFolder}
        />
      )}

      {showRenameFolderModal && folderToEdit && (
        <RenameFolderModal
          folder={folderToEdit}
          onClose={() => {
            setShowRenameFolderModal(false);
            setFolderToEdit(null);
          }}
          onRename={handleRenameFolder}
        />
      )}

      {showDeleteFolderModal && folderToEdit && (
        <DeleteFolderConfirmModal
          folder={folderToEdit}
          onClose={() => {
            setShowDeleteFolderModal(false);
            setFolderToEdit(null);
          }}
          onDelete={handleDeleteFolder}
        />
      )}

      {showNewRequestModal && (
        <NewDocumentRequestModal
          clientId={clientId}
          folders={folders}
          onClose={() => setShowNewRequestModal(false)}
          onCreated={handleCreateRequest}
        />
      )}
    </TabContainer>
  );
};

export default NewDocumentsTab;
